import moment from 'moment'
import createSelector from 'lib/createSelector'
import { filterPromotionsByTravelPeriod, filterPromotionsByLos } from 'lib/bedbank/bedbankPromotions'
import { MONTH_NAMES } from 'constants/dates'
import { addDays, shortDateFormat, dateIsBefore, diffInDays, ymdFormatLocalDate, pad } from 'lib/dateUtils'
import { OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { min, max } from 'lib/arrayUtils'
import { getFlightCalendar } from 'selectors/flightsSelectors'
import { getBedbankCalendarKey } from 'actions/BedbankOfferActions'
import config from 'constants/config'
import { BEDBANK_CALENDAR_FUTURE_DAYS } from 'lib/calendarUtils'

const FLIGHT_MAX_DAYS_IN_FUTURE = 310
const maxFlightDate = moment().add(FLIGHT_MAX_DAYS_IN_FUTURE, 'days').toDate()
const LOS_BASED_CACHE_BUFFER = 16

export const isBedbankOfferCached = createSelector(
  (state: App.State) => state.offer.offersLoading,
  (state: App.State) => state.offer.bedbankOffers,
  (state: App.State, offerId: string) => offerId,
  (offersLoading, offersLoaded, offerId) => !!(offersLoading[offerId] || offersLoaded[offerId]),
)

export const getFullOrSummaryBedbankOffers = createSelector(
  (state: App.State) => state.offer.bedbankOffers,
  (state: App.State) => state.offer.bedbankOfferSummaries,
  (offers, offerSummaries) => ({
    ...offerSummaries,
    ...offers,
  }),
)

export const getPropertyIdIfBestOfferRedirectRequired = createSelector(
  (state: App.State, offerId: string) => state.offer.bedbankOffers[offerId],
  (offer) => {
    if (
      offer &&
      offer.property
    ) {
      return offer.property.id
    }
  },
)

export const getPropertyBestAlternateOfferIds = createSelector(
  (state: App.State, offerId: string) => getPropertyIdIfBestOfferRedirectRequired(state, offerId),
  (state: App.State) => state.offer.alternativeOffersForProperty,
  (propertyId, alternativeOffersForProperty) => {
    if (propertyId) {
      return alternativeOffersForProperty[propertyId]
    }
  },
)

export const getPropertyBestAlternateOffer = createSelector(
  (state: App.State, offerId: string) => getPropertyBestAlternateOfferIds(state, offerId),
  (state: App.State) => state.offer.offers,
  (alternatePropertyOffers, offers) => {
    if (alternatePropertyOffers && alternatePropertyOffers.le && alternatePropertyOffers.le.length > 0) {
      let alt = alternatePropertyOffers.le.find((offer:string) => offers[offer]?.type === 'hotel')
      if (!alt) {
        alt = alternatePropertyOffers.le[0]
      }
      if (offers[alt]) {
        return offers[alt]
      }
    }
  },
)

export const getPropertyBestAlternateURL = createSelector(
  (state: App.State, offerId: string) => getPropertyBestAlternateOffer(state, offerId),
  (state: App.State) => state.geo.currentRegionCode,
  (offer, regionCode) => {
    if (offer) {
      return `/${regionCode.toLowerCase()}/offer/${offer.slug}/${offer.id}`
    }
  },
)

export const getPromotionMinLos = createSelector(
  (state: OfferPageState) => state.bedbankOffer,
  (offer) => {
    if (offer) {
      return offer.promotions.length > 0 ? Math.min(...offer.promotions.map(x => x.los)) : 0
    }

    return 0
  },
)

export const getActivePromotions = createSelector(
  (state: OfferPageState) => state.bedbankOffer,
  (state: OfferPageState) => state.duration,
  (offer, duration) => {
    if (offer) {
      return offer.promotions.filter(promo => filterPromotionsByLos(promo, duration))
    }

    return []
  },
)

interface BaseCalendarDay {
  day: number;
  month: string;
  year: string;
  monthKey: string;
  checkIn: string;
  checkOut: string;
  hasPromo: boolean;
}

interface BaseCalendarMonth {
  key: string;
  month: string;
  year: number;
  days: BaseCalendarDay[];
  hasPromo: boolean;
  cheapestDay: BaseCalendarDay | null;
}

interface BaseCalendar {
  [monthKey: string]: BaseCalendarMonth;
}

export const getBaseCalendar: (state) => BaseCalendar = createSelector(
  getActivePromotions,
  (state: OfferPageState) => state.duration,
  (promotions, duration) => {
    const output: BaseCalendar = {}

    for (let day = 0; day <= BEDBANK_CALENDAR_FUTURE_DAYS; day++) {
      const currentDay = addDays(new Date(), day)
      // use local date format to avoid timezone jump issues
      const month = currentDay.getMonth()
      const year = currentDay.getFullYear()
      const date = currentDay.getDate()
      const monthText = MONTH_NAMES[month]
      const monthKey = `${year}-${pad(month + 1)}`
      const checkIn = ymdFormatLocalDate(currentDay)
      const checkOut = ymdFormatLocalDate(addDays(currentDay, duration ?? 1))
      const hasPromo = promotions.some(promo => filterPromotionsByTravelPeriod(promo, checkIn, checkOut))

      // create month key object if it doesn't exist
      if (!output[monthKey]) {
        output[monthKey] = {
          key: monthKey,
          month: monthText,
          year,
          days: [] as BaseCalendarDay[],
          hasPromo,
          cheapestDay: null,
        }
      }

      // update with current day
      output[monthKey].days.push({
        day: date,
        month: monthText,
        year: year.toString(),
        checkIn,
        checkOut,
        hasPromo,
      } as BaseCalendarDay)
      output[monthKey].hasPromo = output[monthKey].hasPromo || hasPromo
    }

    return output
  },
)

export const getBedbankCalendar = createSelector(
  getPromotionMinLos,
  (state: OfferPageState) => state.bedbankOffer,
  (_state: OfferPageState, appState: App.State) => appState.offer.bedbankOfferCalendar,
  (_state: OfferPageState, appState: App.State) => appState.geo.currentRegionCode,
  (promotionMinLos, offer, bedbankOfferCalendar, region) => {
    return bedbankOfferCalendar[offer?.id]?.[getBedbankCalendarKey(region, promotionMinLos)]
  },
)

export const isBedbankCalendarFetching = createSelector(
  (state: OfferPageState, appState: App.State) => appState.offer.bedbankOfferCalendarLoading[state.bedbankOffer.id],
  (fetching) => !!fetching,
)

export const getCalendarDates = createSelector(
  getBaseCalendar,
  getActivePromotions,
  (state: OfferPageState, appState: App.State) => getBedbankCalendar(state, appState),
  (state: OfferPageState, appState: App.State) => getFlightCalendar(state, appState),
  (state: OfferPageState) => state.rooms,
  (state: OfferPageState) => state.duration,
  (baseCalendarDates, promotions, calendar, flightsCalendar, rooms, duration) => {
    const copyCalendar: { [month: string]: App.BedbankCalendarMonth } = JSON.parse(JSON.stringify(baseCalendarDates))

    if (calendar && flightsCalendar && baseCalendarDates && promotions.length > 0) {
      for (const checkIn of Object.keys(flightsCalendar)) {
        const monthKey = shortDateFormat(new Date(checkIn))
        if (copyCalendar[monthKey]) {
          const dayData = copyCalendar[monthKey].days.find(x => x.checkIn === checkIn)
          if (dayData?.hasPromo && flightsCalendar[checkIn].cost && dateIsBefore(new Date(dayData.checkOut), maxFlightDate)) {
            dayData.flightPrice = flightsCalendar[checkIn].cost
          }
        }
      }
    }

    if (calendar && baseCalendarDates && promotions.length > 0) {
      for (const day of calendar.dates) {
        const monthKey = shortDateFormat(new Date(day.checkIn))
        if (copyCalendar[monthKey]) {
          const dayData = copyCalendar[monthKey].days.find(x => x.checkIn === day.checkIn)
          if (dayData?.hasPromo) {
            dayData.price = Math.ceil(day.inclusive * rooms.length * (duration ?? 1))
            copyCalendar[monthKey].cheapestDay = min(copyCalendar[monthKey].days.filter(d => d.price), d => d.price)
            copyCalendar[monthKey].cheapestDayWithFlights = min(copyCalendar[monthKey].days.filter(d => d.price), d => d.price + (d.flightPrice ?? 0))
          }
        }
      }
    }

    return copyCalendar
  },
)

export const getSelectedDay = createSelector(
  getCalendarDates,
  (state: OfferPageState) => state.checkInDate,
  (state: OfferPageState) => state.checkOutDate,
  (state: OfferPageState) => state.duration,
  (calendarDates, checkIn, checkOut, duration) => {
    if (checkIn && checkOut && duration && calendarDates) {
      const isoCheckIn = checkIn.format(ISO_DATE_FORMAT)
      if (calendarDates[shortDateFormat(new Date(isoCheckIn))]) {
        return calendarDates[shortDateFormat(new Date(isoCheckIn))].days.find((day) => day.checkIn === isoCheckIn)
      }
    }
    return undefined
  },
)

export const getLowestPriceDay = createSelector(
  (state: OfferPageState, appState: App.State) => getBedbankCalendar(state, appState),
  (state: OfferPageState) => state.rooms,
  (calendar, rooms) => {
    if (calendar && rooms) {
      return min(calendar.rooms, d => d.inclusive * rooms.length)
    }
    return undefined
  },
)

export const generateDurations = createSelector(
  getPromotionMinLos,
  getLowestPriceDay,
  getActivePromotions,
  getSelectedDay,
  getBedbankCalendar,
  (minLOS, lowestPrice, promotions, selectedDay, calendarData) => {
    const defaultDayRestriction = minLOS + LOS_BASED_CACHE_BUFFER
    let dayRestriction = defaultDayRestriction
    let lowestRoomDurations
    if (promotions) {
      const lastDate = max(promotions.map(x => x.travelPeriod.to), (d) => new Date(d).getDate())
      if (lastDate) {
        const allDays = diffInDays(new Date(), new Date(lastDate))
        if (!selectedDay && allDays <= (dayRestriction * 2)) {
          if (allDays > minLOS) {
            dayRestriction = allDays
          } else {
            dayRestriction = 0
          }
        } else if (selectedDay) {
          const daysCheckout = diffInDays(new Date(selectedDay.checkIn), new Date(lastDate))
          if (daysCheckout > 0) {
            dayRestriction = daysCheckout
          } else {
            dayRestriction = 0
          }
        }
      }
    }

    if (calendarData) {
      lowestRoomDurations = Array.from({ length: dayRestriction }).map((_, index) => {
        let lowest
        calendarData.rooms.forEach(r => {
          // future state of bedbank calendar will have inclusive price for los
          // @ts-ignore
          if (!lowest || (r.los && r.los[index + 1]?.inclusive < lowest)) {
            // @ts-ignore
            lowest = r.los ? r.los[index + 1]?.inclusive : undefined
          }
        })
        return lowest ? Math.ceil(lowest) : undefined
      })
    }

    return Array.from({ length: defaultDayRestriction }).map((_, index) => ({
      duration: index + 1,
      hasPromo: minLOS <= index + 1 && dayRestriction >= index + 1,
      price: lowestRoomDurations[index] ?? (minLOS <= index + 1 && lowestPrice ? Math.ceil(Number(lowestPrice.inclusive) * (index + 1)) : undefined),
    }))
  },
)

export const isFlashCalendarEnabled = createSelector(
  (state: OfferPageState) => state.bedbankOffer,
  (state: OfferPageState, appState: App.State) => getBedbankCalendar(state, appState),
  (offer, calendar) => {
    if (config.BEDBANK_NEW_CALENDAR && offer && calendar) {
      return calendar.rooms.length > 0 && offer.promotions.length > 0
    }
    return false
  },
)
